/**
 * #830 Part 2 — every row of the uploaded file is accounted for.
 *
 * The wizard sends the API only the rows it keeps. Before this, the rows the
 * admin removed on the Review step, the rows it could not read and — with
 * "Skip duplicates" — the duplicate rows never reached the API, so the
 * import's total_rows counted only what was sent and Import History
 * under-reported the file. `planImportRows` decides which rows are sent AND
 * describes the rest, in one pass that gives every row of the file exactly one
 * place, so the summary always adds up:
 *
 *   rowsInFile = rows sent + removedBeforeImport + unreadableInWizard
 *                + duplicatesSkippedInWizard
 *
 * The API checks the same sum (apps/api/src/modules/recipients/import/
 * import-file-summary.ts, fileSummaryProblem) and refuses a summary that does
 * not add up with errorCode IMPORT_FILE_SUMMARY_INVALID.
 */
import type { CSVWizardState } from './csv-import-wizard'

/**
 * The most unreadable rows one upload lists one by one — the API's
 * MAX_LISTED_UNREADABLE_ROWS (apps/api/.../dto/recipient-import-response.dto.ts).
 * The rest are counted, not listed.
 */
export const MAX_LISTED_UNREADABLE_ROWS = 1000

/** The longest reason the API accepts for one unreadable row (MAX_UNREADABLE_REASON_LENGTH). */
export const MAX_UNREADABLE_REASON_LENGTH = 300

/** The API's errorCode for a summary that does not account for the file. */
export const IMPORT_FILE_SUMMARY_INVALID = 'import_file_summary_invalid'

/**
 * Stored as the row's error message when the analysis gave no reason. Data,
 * like the analysis's own reasons ("Email is empty"): it is kept with the
 * import, not shown through the catalogue.
 */
const NO_REASON = 'Could not be read'

/** What the wizard did with every row of the file it did not send (UploadImportDto.fileSummary). */
export interface ImportFileSummaryPayload {
  /** Data rows in the uploaded file. */
  rowsInFile: number
  /** Rows the admin removed on the Review step. */
  removedBeforeImport: number
  /** Rows the wizard could not read. */
  unreadableInWizard: number
  /** Rows skipped as duplicates with "Skip duplicates" (or "Skip & Export"). */
  duplicatesSkippedInWizard: number
  /** The unreadable rows, lowest first, at most MAX_LISTED_UNREADABLE_ROWS; `row` is the zero-based file index. */
  unreadableRows: Array<{ row: number; reason: string }>
}

const NO_ROWS: readonly number[] = []

type PlanInput = Pick<
  CSVWizardState,
  'parsedData' | 'duplicateAnalysis' | 'deletedRowIndices' | 'duplicateHandling'
>

function reasonFor(reason: string | undefined): string {
  const text = typeof reason === 'string' ? reason.trim() : ''
  return (text || NO_REASON).slice(0, MAX_UNREADABLE_REASON_LENGTH)
}

/**
 * The rows to send, in today's order (new rows, then — in Update mode — the
 * duplicate rows), and the summary of every other row.
 *
 * Each row of the file lands in exactly one bucket, first match wins:
 *   removed by the admin → removedBeforeImport
 *   chosen to send       → sent
 *   unreadable           → unreadableInWizard (with its reason)
 *   duplicate            → duplicatesSkippedInWizard
 *   in no list at all    → sent (appended in file order), so the API's own
 *                          analysis reads it rather than it being lost. The
 *                          analysis and the review table's sync partition
 *                          every row, so this does not happen today.
 * A removed row the analysis still lists as unreadable or duplicate (the
 * review table syncs 300 ms after a delete) therefore counts once, as removed.
 */
export function planImportRows(state: PlanInput): {
  rowIndices: number[]
  summary: ImportFileSummaryPayload
} {
  const n = state.parsedData.length
  const inFile = (i: number) => Number.isInteger(i) && i >= 0 && i < n
  const analysis = state.duplicateAnalysis
  const newRowIndices = analysis ? analysis.newRowIndices : NO_ROWS
  const deleted = new Set(state.deletedRowIndices.filter(inFile))
  const duplicateRows = analysis ? analysis.duplicateDetails.map((d) => d.rowIndex).filter(inFile) : NO_ROWS
  const reasons = new Map<number, string>()
  for (const d of analysis ? analysis.invalidDetails : []) {
    if (inFile(d.rowIndex) && !reasons.has(d.rowIndex)) reasons.set(d.rowIndex, d.reason)
  }
  const duplicates = new Set(duplicateRows)

  // The send order the wizard has always used.
  const rowIndices: number[] = []
  const sent = new Set<number>()
  const send = (i: number) => {
    if (!inFile(i) || deleted.has(i) || sent.has(i)) return
    sent.add(i)
    rowIndices.push(i)
  }
  for (const i of newRowIndices) send(i)
  if (state.duplicateHandling === 'Update') for (const i of duplicateRows) send(i)

  let removedBeforeImport = 0
  let duplicatesSkippedInWizard = 0
  const unreadable: Array<{ row: number; reason: string }> = []
  for (let i = 0; i < n; i += 1) {
    if (deleted.has(i)) removedBeforeImport += 1
    else if (sent.has(i)) continue
    else if (reasons.has(i)) unreadable.push({ row: i, reason: reasonFor(reasons.get(i)) })
    else if (duplicates.has(i)) duplicatesSkippedInWizard += 1
    else send(i)
  }

  return {
    rowIndices,
    summary: {
      rowsInFile: n,
      removedBeforeImport,
      unreadableInWizard: unreadable.length,
      duplicatesSkippedInWizard,
      // Built in file order, so already lowest first.
      unreadableRows: unreadable.slice(0, MAX_LISTED_UNREADABLE_ROWS),
    },
  }
}
